import type { LatLng } from '@cobliteam/api-lib';
import type { UUID } from './identifier';
import type { VehicleSize } from './vehicle';

export interface DeviceDTO {
  id: UUID;
  cobliId: string;
  type: string;
  generalType: string;
}

export interface VehicleGroupDTO {
  id: UUID;
  name: string;
}

export interface VehicleDTO {
  id: UUID;
  licensePlate?: string;
  brand?: string;
  model?: string;
  group?: VehicleGroupDTO;
  groups?: VehicleGroupDTO[];
  year?: string;
}

export interface VehicleDTOV2 extends VehicleDTO {
  tags?: string[];
  size?: VehicleSize;
}

export interface DriverDTO {
  id: UUID;
  name?: string;
}

export enum Severity {
  MEDIUM = 'Média',
  SEVERE = 'Grave',
  VERY_SEVERE = 'Gravíssima',
  DEFAULT = 'Default',
}

export enum SpeedEventType {
  SpeedLimitExceed = 'ROAD_SPEED_EVENT',
  VehicleSpeedLimitExceed = 'VEHICLE_SPEED_EVENT',
}

export enum SpeedEventReportGrouping {
  DRIVER = 'driver',
  VEHICLE = 'vehicle',
  VEHICLE_GROUP = 'group',
}

export interface EventsSeverity {
  mediumEvents: number;
  severeEvents: number;
  verySevereEvents: number;
}

export interface DriverRoadSpeedEvents {
  driverId: UUID;
  name?: string;
  severity: EventsSeverity;
  speedEventsCount: number;
}

export interface VehicleSpeedEvents {
  speedEventsCount: number;
  durationAboveSpeedLimitInMillis: number;
  drivenDurationInMillis: number;
}

export interface DriverVehicleSpeedEvents extends VehicleSpeedEvents {
  driverId: UUID;
  name?: string;
}

// New speeding types (the above ones should be removed after complete migration)

export interface SpeedEvent {
  eventTime: number;
  vehicleId: UUID;
  groupId?: UUID;
  deviceId: string;
  driverId?: UUID;
  location: LatLng;
  speedInKmh: number;
  speedLimitInKmh: number;
  durationAboveInMillis?: number;
  severity: Severity;
  startTime?: number;
  medianSpeedInKmh?: number;
  medianBasedSeverity?: Severity;
  type: 'road' | 'vehicle';
}

export interface SpeedEventWithName extends SpeedEvent {
  driverName?: string;
}

export interface RoadSpeedEvent extends SpeedEvent {}

export interface VehicleSpeedEvent extends SpeedEvent {}

export enum SpeedReportFileFormat {
  Excel = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  Csv = 'text/csv',
}

export class SpeedReportType {
  public static readonly excel = new SpeedReportType(SpeedReportFileFormat.Excel, 'excel');

  public static readonly csv = new SpeedReportType(SpeedReportFileFormat.Csv, 'csv');

  private constructor(
    public readonly fileFormat: SpeedReportFileFormat,
    public readonly displayValue: string
  ) {}
}

export enum PageType {
  DRIVER = 'driver',
  VEHICLE = 'vehicle',
  VEHICLE_GROUP = 'vehicleGroup',
}

export interface SpeedingSummary {
  kmsPerEvent: number;
  timeAboveLimitInMinutes: number;
  totalTripDurationInMinutes: number;
  mediumEventsCount: number;
  highEventsCount: number;
  higherEventsCount: number;
}

interface SpeedingTableRow {
  kmsPerEvent: number;
  mediumEventsCount: number;
  highEventsCount: number;
  higherEventsCount: number;
  totalEventsCount: number;
  evolution: number;
  percentageAboveLimit: number;
  totalTimeAboveLimitInMinutes: number;
  totalTripDurationInMinutes: number;
  totalTripDistanceInKms: number;
}

export interface VehicleSpeedingTableRow extends SpeedingTableRow {
  vehicle: VehicleDTO;
  device?: DeviceDTO;
}

export interface DriverSpeedingTableRow extends SpeedingTableRow {
  driver: DriverDTO;
}
